import type { JokerDefinition, RankDefinition, RoomProjection } from "@quizshow/protocol";
import { RoomImage } from "./media";

export function currentRank(ranks: RankDefinition[], progress: number): RankDefinition | undefined {
  return ranks
    .filter((rank) => rank.requiredProgress <= progress)
    .sort((left, right) => right.requiredProgress - left.requiredProgress)[0];
}

export function rankValue(
  projection: Pick<RoomProjection, "scoringMode">,
  score: number,
  rankProgress: number,
): number {
  return projection.scoringMode === "points" ? score : rankProgress;
}

export function ScoreStatus({
  projection,
  score,
  rankProgress,
  roomId,
  token,
}: {
  projection: RoomProjection;
  score: number;
  rankProgress: number;
  roomId?: string;
  token?: string;
}) {
  if (projection.scoringMode === "rank-progress") {
    const rank = currentRank(projection.ranks, rankProgress);
    return (
      <span
        className="rank-badge"
        style={{ color: rank?.color, backgroundColor: rank?.backgroundColor }}
        title={`${rankProgress} Fortschritt`}
      >
        {rank?.imageMediaId ? (
          <RoomImage roomId={roomId} token={token} mediaId={rank.imageMediaId} className="rank-icon" />
        ) : null}
        {rank?.name ?? "Noch ohne Rang"} · Fortschritt {rankProgress}
        {score ? ` · ${score} Bonuspunkte` : ""}
      </span>
    );
  }
  return <span>{score} Punkte</span>;
}

export function RankSpotlight({
  ranks,
  progress,
  unitLabel = "Fortschritt",
  roomId,
  token,
}: {
  ranks: RankDefinition[];
  progress: number;
  unitLabel?: string;
  roomId?: string;
  token?: string;
}) {
  if (!ranks.length) return null;
  const rank = currentRank(ranks, progress);
  return (
    <div
      className="rank-spotlight"
      style={{ color: rank?.color, backgroundColor: rank?.backgroundColor }}
      aria-label={`Aktueller Rang: ${rank?.name ?? "Noch ohne Rang"}`}
    >
      {rank?.imageMediaId ? (
        <RoomImage roomId={roomId} token={token} mediaId={rank.imageMediaId} className="rank-spotlight__image" />
      ) : (
        <span className="rank-spotlight__fallback">{rank?.name?.slice(0, 1) || "?"}</span>
      )}
      <span>
        <small>Aktueller Rang</small>
        <strong>{rank?.name ?? "Noch ohne Rang"}</strong>
        <b>{progress} {unitLabel}</b>
      </span>
    </div>
  );
}

export function JokerStatus({
  jokers,
  counts,
  roomId,
  token,
}: {
  jokers: JokerDefinition[];
  counts: Record<string, number>;
  roomId?: string;
  token?: string;
}) {
  if (!jokers.length) return null;
  return (
    <span className="joker-status">
      {jokers.map((joker) => (
        <span className="joker-chip" key={joker.jokerId} style={{ borderColor: joker.color }}>
          {joker.imageMediaId ? (
            <RoomImage roomId={roomId} token={token} mediaId={joker.imageMediaId} className="joker-icon" />
          ) : null}
          {joker.icon ? `${joker.icon} ` : ""}
          {joker.name}: {counts[joker.jokerId] ?? 0}
        </span>
      ))}
    </span>
  );
}

export function BonusRewardStatus({ projection }: { projection: RoomProjection }) {
  const reward = projection.question?.bonusReward;
  if (projection.question?.questionType !== "bonus" || !reward) return null;
  const label =
    reward.type === "joker"
      ? projection.jokers.find((joker) => joker.jokerId === reward.jokerId)?.name ?? "Joker"
      : reward.type === "life"
        ? "Leben"
        : "Punkte";
  return <span className="bonus-reward">Belohnung: {reward.amount} × {label}</span>;
}

export function shouldShowLives(projection: RoomProjection): boolean {
  return projection.usesLives;
}

export function LivesStatus({
  projection,
  lives,
}: {
  projection: RoomProjection;
  lives: number;
}) {
  return shouldShowLives(projection) ? <span>{lives} Leben</span> : null;
}
